import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useGetIdentity } from "ra-core";

import { getSupabaseClient } from "../providers/supabase/supabase";
import { Softphone, type SoftphoneStatus } from "./softphone";

export interface SoftphoneContextValue {
  status: SoftphoneStatus;
  remoteNumber: string | null;
  enabled: boolean;
  call: (number: string) => void;
  answer: () => void;
  decline: () => void;
  hangup: () => void;
  toggleMute: () => boolean;
}

const SoftphoneContext = createContext<SoftphoneContextValue>({
  status: "offline",
  remoteNumber: null,
  enabled: false,
  call: () => {},
  answer: () => {},
  decline: () => {},
  hangup: () => {},
  toggleMute: () => false,
});

export const useSoftphone = () => useContext(SoftphoneContext);

const SIP_WSS_URL =
  import.meta.env.VITE_SIP_WSS_URL || "wss://crm.k-panel.ru/asterisk/ws";

export const SoftphoneProvider = ({ children }: { children: ReactNode }) => {
  const { identity } = useGetIdentity();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const phoneRef = useRef<Softphone | null>(null);
  const [state, setState] = useState<{
    status: SoftphoneStatus;
    remoteNumber: string | null;
  }>({ status: "offline", remoteNumber: null });
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (!identity?.id || phoneRef.current) return;
    let cancelled = false;

    const boot = async () => {
      // sip_accounts RLS returns only the current user's row
      const { data, error } = await getSupabaseClient()
        .from("sip_accounts")
        .select("username, password")
        .maybeSingle();
      if (cancelled || error || !data) return;

      const audio = audioRef.current;
      if (!audio) return;
      const phone = new Softphone(audio);
      phoneRef.current = phone;
      phone.onChange((snapshot) => setState({ ...snapshot }));
      try {
        await phone.connect(SIP_WSS_URL, data.username, data.password);
        setEnabled(true);
      } catch (err) {
        console.error("softphone connect failed", err);
      }
    };

    boot();
    return () => {
      cancelled = true;
    };
  }, [identity?.id]);

  useEffect(() => {
    return () => {
      phoneRef.current?.destroy();
      phoneRef.current = null;
    };
  }, []);

  const value = useMemo<SoftphoneContextValue>(
    () => ({
      status: state.status,
      remoteNumber: state.remoteNumber,
      enabled,
      call: (number: string) => phoneRef.current?.call(number),
      answer: () => phoneRef.current?.answer(),
      decline: () => phoneRef.current?.decline(),
      hangup: () => phoneRef.current?.hangup(),
      toggleMute: () => phoneRef.current?.toggleMute() ?? false,
    }),
    [state, enabled],
  );

  return (
    <SoftphoneContext.Provider value={value}>
      {children}
      <audio ref={audioRef} autoPlay />
    </SoftphoneContext.Provider>
  );
};
